import {
  ANALYST_SAMPLE_FLOOR,
  measuredRow,
  ratePercent,
  type AggregateLabel,
  type AggregateRow,
  type AnalystQueryId,
  type QueryResult,
  type QueueSummary,
} from "./analyst";
import {
  COMPETITOR_PIECE_TYPES,
  COMPETITOR_SOURCE_KINDS,
  isCompetitorPieceType,
  isCompetitorSourceKind,
  type CompetitorPieceType,
  type CompetitorSourceKind,
} from "./competitorIntel";
import { formatCents } from "./figures";
import { RELEASED_GAPS_TITLE } from "./knowledgeGaps";
import type { SalesEntryTally, SalesMeasure, SalesSpread } from "./salesMeasure";
import { OUTCOMES, type Outcome } from "./types";

/**
 * W1 ANALYST: the nine aggregates, as pure functions over rows the db loaders
 * (`db/analystAggregates.ts`) hand over. Every function here is deterministic
 * code (CLAUDE.md section 4: math never goes through a model).
 *
 * Rows carry ids, codes and cents. None carries a customer word or name, so
 * nothing a function returns can leak one (section 10).
 *
 * A cents sum or a rate under `ANALYST_SAMPLE_FLOOR` rows reads `not_measured`,
 * through `measuredRow`, never zero. A count is a fact whatever its size.
 */

const result = (queryId: AnalystQueryId, rows: readonly AggregateRow[], n: number, label: AggregateLabel): QueryResult => ({
  queryId,
  rows,
  n,
  label,
});

const sum = (xs: readonly number[]): number => xs.reduce((a, b) => a + b, 0);

// ------------------------------------------------------------------- deposits

export interface DepositFact {
  readonly label: Outcome;
  readonly inquiryType: string | null;
  readonly amountCents: number | null;
}

/** Deposits per inquiry type: a count row and a cents row each, standing outcomes only. */
export function depositsByInquiryType(rows: readonly DepositFact[]): QueryResult {
  const deposits = rows.filter((r) => r.label === "deposit");
  const groups = new Map<string, DepositFact[]>();
  for (const d of deposits) {
    const key = d.inquiryType ?? "not_recorded";
    groups.set(key, [...(groups.get(key) ?? []), d]);
  }
  const ordered = [...groups.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  const out: AggregateRow[] = [];
  for (const [key, g] of ordered) {
    out.push(measuredRow(key, g.length, g.length, "count"));
    const withAmount = g.filter((x) => x.amountCents !== null);
    // No row with an amount is no sum at all, not a sum of zero.
    out.push(measuredRow(`${key}:cents`, withAmount.length, withAmount.length === 0 ? null : sum(withAmount.map((x) => x.amountCents ?? 0)), "cents"));
  }
  return result("deposits_by_inquiry_type", out, deposits.length, "count");
}

// -------------------------------------------------------------------- consults

/** Consults held per week key, every requested week present (a week with none is a measured count of 0). */
export function consultsByWeek(weeks: readonly string[], heldByWeek: Readonly<Record<string, number>>): QueryResult {
  const out = weeks.map((w) => measuredRow(w, heldByWeek[w] ?? 0, heldByWeek[w] ?? 0, "count"));
  return result("consults_by_week", out, sum(out.map((r) => r.value ?? 0)), "count");
}

// ------------------------------------------------------------ assisted revenue

export interface AssistFact {
  /** The agent whose draft was last transmitted on the thread before the outcome, or null for owner-only. */
  readonly agentId: string | null;
  readonly label: Outcome;
  readonly amountCents: number | null;
}

/**
 * Cents per assisting agent, one row per label (`<agent>:deposit`, `<agent>:sold`), as the
 * house `tallyOutcomeCents` keeps one bucket per label. They are never added into one figure:
 * a deposit later counted inside a sale would be counted twice. TA-W1-01 (for Kabir): whether a
 * combined figure is ever wanted, and how a deposit that is later sold is treated.
 * An association: the agent was on the thread before the outcome, which says nothing about
 * what would have happened without it (never lift). Owner-only threads are the contrast row.
 */
export function assistedRevenueByAgent(rows: readonly AssistFact[]): QueryResult {
  const money = rows.filter((r) => r.label === "deposit" || r.label === "sold");
  const groups = new Map<string, AssistFact[]>();
  for (const r of money) {
    const key = `${r.agentId ?? "owner_only"}:${r.label}`;
    groups.set(key, [...(groups.get(key) ?? []), r]);
  }
  const out = [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, g]) => {
      const withAmount = g.filter((x) => x.amountCents !== null);
      return measuredRow(key, withAmount.length, withAmount.length === 0 ? null : sum(withAmount.map((x) => x.amountCents ?? 0)), "cents");
    });
  return result("assisted_revenue_by_agent", out, money.length, "association");
}

// ------------------------------------------------------------------- approval

export interface DecisionFact {
  readonly agentId: string;
  readonly scenario: string | null;
  readonly approved: boolean;
}

export function approvalRateByAgentScenario(rows: readonly DecisionFact[]): QueryResult {
  const groups = new Map<string, DecisionFact[]>();
  for (const r of rows) {
    const key = `${r.agentId}/${r.scenario ?? "unrouted"}`;
    groups.set(key, [...(groups.get(key) ?? []), r]);
  }
  const out = [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, g]) => measuredRow(key, g.length, ratePercent(g.filter((x) => x.approved).length, g.length), "percent"));
  return result("approval_rate_by_agent_scenario", out, rows.length, "count");
}

// --------------------------------------------------------------------- stages

export interface StagePairFact {
  readonly from: string;
  readonly to: string;
  /** Leads that reached `from` in the period. */
  readonly reachedFrom: number;
  /** Of those, how many went on to reach `to`. */
  readonly reachedTo: number;
}

export function stageConversion(pairs: readonly StagePairFact[]): QueryResult {
  const out = pairs.map((p) => measuredRow(`${p.from}>${p.to}`, p.reachedFrom, ratePercent(p.reachedTo, p.reachedFrom), "percent"));
  return result("stage_conversion", out, pairs[0]?.reachedFrom ?? 0, "count");
}

// --------------------------------------------------------- gaps and restraint

export interface GapFact {
  readonly topic: string;
  readonly threads: number;
  /**
   * TA-1361: a topic from the released group (answered with a holding line, the fact still missing). Absent or
   * false: a topic from the queue, as before.
   */
  readonly released?: boolean;
}

/**
 * The queue's topics first, then the released group's as their own rows, each keyed with the group's plain name.
 * `n` stays the queue's threads: the two are never folded into one figure (TA-1361).
 */
export function knowledgeGapRows(gaps: readonly GapFact[]): QueryResult {
  const ranked = (xs: readonly GapFact[]): GapFact[] => [...xs].sort((a, b) => b.threads - a.threads || a.topic.localeCompare(b.topic));
  const queued = gaps.filter((g) => g.released !== true);
  const released = gaps.filter((g) => g.released === true);
  const out = [
    ...ranked(queued).map((g) => measuredRow(g.topic, g.threads, g.threads, "count")),
    ...ranked(released).map((g) => measuredRow(`${g.topic} (${RELEASED_GAPS_TITLE})`, g.threads, g.threads, "count")),
  ];
  return result("knowledge_gaps", out, sum(queued.map((g) => g.threads)), "count");
}

export interface RestraintFact {
  readonly gate: string;
  readonly count: number;
}

/** Withheld sends per gate. A silence is a recorded decision (CLAUDE.md section 7), so it is counted like a send. */
export function restraintOutcomeRows(facts: readonly RestraintFact[]): QueryResult {
  const out = [...facts].sort((a, b) => a.gate.localeCompare(b.gate)).map((f) => measuredRow(f.gate, f.count, f.count, "count"));
  return result("restraint_outcomes", out, sum(facts.map((f) => f.count)), "count");
}

// ----------------------------------------------------------- injected readers

const NOT_MEASURED = (key: string): AggregateRow => ({ key, n: 0, value: null, unit: "count", status: "not_measured" });

/** W2's queue, through a reader the caller injects. Absent is not_measured, never zero proposals. */
export function learnerChangeRows(q: QueueSummary | null): QueryResult {
  if (q === null) return result("learner_changes", [NOT_MEASURED("learner")], 0, "count");
  const rows: AggregateRow[] = [
    q.ran ? measuredRow("proposals_waiting", q.pending, q.pending, "count") : { key: "proposals_waiting", n: 0, value: null, unit: "count", status: "not_measured" },
    measuredRow("learner_ran", 1, q.ran ? 1 : 0, "count"),
  ];
  if (!q.ran && q.whyNotRun !== undefined) rows.push(measuredRow(`did_not_run:${q.whyNotRun}`, 1, 1, "count"));
  return result("learner_changes", rows, q.ran ? q.pending : 0, "count");
}

/** W7's scoreboard rows, through a reader the caller injects. Rows pass through unchanged. */
export function scoreboardRows(rows: readonly AggregateRow[] | null): QueryResult {
  if (rows === null) return result("scoreboard", [NOT_MEASURED("scoreboard")], 0, "count");
  return result("scoreboard", rows, Math.max(0, ...rows.map((r) => r.n)), "count");
}

// ---------------------------------------------------- G25: review movement

export interface ReviewRatingFact {
  /** The Monday key of the closed week the review was posted in. */
  readonly week: string;
  readonly rating: number;
}

/**
 * G25 (AB 5.6 "rating, velocity"; MS B10 "review movement"): per closed week, a count for every rating 1 to 5
 * (a week's zero is a measured zero once any review exists), then the week's mean as a whole percent of 5,
 * measured only at `ANALYST_SAMPLE_FLOOR` reviews. No review at all is no rows: empty until a provider exists,
 * never a run of zero weeks. A rating outside 1 to 5 throws; the table's own check should make that impossible.
 */
export function reviewMovementRows(weeks: readonly string[], facts: readonly ReviewRatingFact[]): QueryResult {
  for (const f of facts) {
    if (!Number.isInteger(f.rating) || f.rating < 1 || f.rating > 5) throw new Error(`review_movement read a rating outside 1 to 5 in week ${f.week}`);
  }
  if (facts.length === 0) return result("review_movement", [], 0, "count");
  const rows: AggregateRow[] = [];
  for (const w of weeks) {
    const inWeek = facts.filter((f) => f.week === w);
    for (const stars of [1, 2, 3, 4, 5]) {
      const c = inWeek.filter((f) => f.rating === stars).length;
      rows.push(measuredRow(`${w}:stars_${stars}`, c, c, "count"));
    }
    rows.push(measuredRow(`${w}:mean_pct_of_5`, inWeek.length, ratePercent(sum(inWeek.map((f) => f.rating)), 5 * inWeek.length), "percent"));
  }
  return result("review_movement", rows, facts.filter((f) => weeks.includes(f.week)).length, "count");
}

// ------------------------------------------------- G54: the sales measure's figures

/**
 * A duration in whole seconds, floored like a rate: below `ANALYST_SAMPLE_FLOOR` responses, or with no sample,
 * it reads not_measured. The unit is "count" with `_seconds` in the key, since the row vocabulary has no
 * duration unit; the key says what the number is.
 */
function secondsRow(key: string, n: number, ms: number | null): AggregateRow {
  if (ms === null || n < ANALYST_SAMPLE_FLOOR) return { key, n, value: null, unit: "count", status: "not_measured" };
  return { key, n, value: Math.floor(ms / 1000 + 0.5), unit: "count", status: "measured" };
}

/**
 * G54 (MS B10 "response times"): `pnpm measure:sales`'s first response, from the same `measureSales` over the
 * same `loadSalesEvidence`, so the digest, the Q&A and the CLI read one figure. The headline definition: a new
 * inquiry's first customer message to the first shop or agent message that left (F-06). Medians are the upper
 * middle, as the CLI's are.
 */
export function responseTimeRows(m: SalesMeasure): QueryResult {
  const fr = m.activity.firstResponse;
  const spread = (name: string, s: SalesSpread): AggregateRow[] => [
    secondsRow(`${name}:median_seconds`, s.count, s.medianMs),
    secondsRow(`${name}:p90_seconds`, s.count, s.p90Ms),
  ];
  const rows: AggregateRow[] = [
    measuredRow("answered", fr.answered, fr.answered, "count"),
    measuredRow("unanswered", fr.unanswered, fr.unanswered, "count"),
    measuredRow("unanswered_with_released_draft", fr.unansweredWithReleasedDraft, fr.unansweredWithReleasedDraft, "count"),
    ...spread("all", fr.all),
    ...spread("owner_first", fr.byOwner),
    ...spread("agent_first", fr.byAgent),
  ];
  return result("response_time", rows, fr.answered + fr.unanswered, "count");
}

/**
 * G54 (IC "this reel produced 14 conversations and 2 deposits"): the sales measure's entry attribution, per story
 * or post id, then the overflow, unprintable, later-source and no-source lines the CLI prints. New inquiries and
 * each outcome label recorded on them are counts; deposit and sold cents are floored on the rows with an amount,
 * never summed across labels (TA-504). Where a thread started, not what caused a sale: an association.
 */
export function entrySourceRows(m: SalesMeasure): QueryResult {
  if (m.activity.newInquiries === 0) return result("entry_sources", [], 0, "association");
  const e = m.entryAttribution;
  const lines: [string, SalesEntryTally][] = [
    ...e.sources.map((s): [string, SalesEntryTally] => [s.entrySource, s]),
    ...(e.overflow.sources > 0 ? [["overflow", e.overflow] as [string, SalesEntryTally]] : []),
    ...(e.unprintable.inquiries > 0 ? [["unprintable", e.unprintable] as [string, SalesEntryTally]] : []),
    ...(e.laterSource.inquiries > 0 ? [["later_source", e.laterSource] as [string, SalesEntryTally]] : []),
    ["no_source", e.noSource],
  ];
  const rows: AggregateRow[] = [];
  for (const [key, t] of lines) {
    rows.push(measuredRow(`${key}:inquiries`, t.inquiries, t.inquiries, "count"));
    for (const label of OUTCOMES) {
      const b = t.byLabel[label];
      if (b.count === 0) continue;
      rows.push(measuredRow(`${key}:${label}`, b.count, b.count, "count"));
      if (label === "deposit" || label === "sold") {
        const withAmount = b.count - b.withoutAmount;
        rows.push(measuredRow(`${key}:${label}:cents`, withAmount, withAmount === 0 ? null : b.amountCents, "cents"));
      }
    }
  }
  return result("entry_sources", rows, m.activity.newInquiries, "association");
}

// ------------------------------------------- TA-1336: what customers say other places charge

/**
 * One recorded competitor mention as the loader hands it over (`competitor_mentions`, 0065). No message text.
 * The loader hands them over oldest first; `competitorPieceSummaries` relies on that order to keep a customer's
 * newest price (TA-1361).
 */
export interface CompetitorMentionFact {
  /** The customer, the unit every count below is measured in: one contact can hold one thread per channel (TA-1361). */
  readonly contactId: string;
  readonly threadId: string;
  readonly pieceType: string;
  readonly sourceKind: string;
  /** The shop or site name the customer wrote, or null. Shown on the live page only, never in a digest cell. */
  readonly sourceName: string | null;
  readonly claimedPriceCents: number | null;
}

/** One priced catalog product whose `specs.style` names a piece type exactly (`pieceTypeOfStyle`). */
export interface ShopPriceFact {
  readonly pieceType: CompetitorPieceType;
  readonly priceCents: number;
}

export interface CentsSpread {
  readonly low: number;
  readonly median: number;
  readonly high: number;
}

export type CompetitorPosition = "below" | "within" | "above";

/**
 * Every customer count here is distinct contacts, never messages (section 0.6, TA-1361). The two
 * exceptions count the shop's own priced catalog products, not customers: yours.n and yoursPriced.
 */
export interface CompetitorPieceSummary {
  readonly pieceType: CompetitorPieceType;
  /** Distinct customers who named another shop or site for this piece type. */
  readonly mentions: number;
  /** Distinct customers per kind; a customer who named two kinds counts once in each, so these can sum past `mentions`. */
  readonly bySourceKind: Readonly<Record<CompetitorSourceKind, number>>;
  /** At most `COMPETITOR_TOP_SOURCES`, by distinct customers, then name; names grouped without regard to case. */
  readonly topSources: readonly { readonly name: string; readonly count: number }[];
  /** Distinct customers who gave a price; each customer's newest price is the one kept. */
  readonly priced: number;
  /** Null below `ANALYST_SAMPLE_FLOOR` customers who gave a price. */
  readonly reported: CentsSpread | null;
  /** The shop's own priced pieces of this type; null below `ANALYST_SAMPLE_FLOOR`. */
  readonly yours: (CentsSpread & { readonly n: number }) | null;
  /** The shop's own priced catalog products of this type, whatever the floor: the n of the `yours_*` cells (TA-1361). */
  readonly yoursPriced: number;
  /** Where the shop's middle price sits against the reported range; null unless both sides are measured. */
  readonly position: CompetitorPosition | null;
}

/** How many named sources the page shows per piece type. AWAITING RATIFICATION. */
export const COMPETITOR_TOP_SOURCES = 3;

/** The upper middle of a non-empty list of integer cents (the sales measure's convention): never an average. */
export function medianCents(xs: readonly number[]): number {
  if (xs.length === 0) throw new Error("medianCents of nothing: a caller must floor before asking for a median");
  const sorted = [...xs].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] as number;
}

function centsSpread(xs: readonly number[]): CentsSpread | null {
  if (xs.length < ANALYST_SAMPLE_FLOOR) return null;
  const sorted = [...xs].sort((a, b) => a - b);
  return { low: sorted[0] as number, median: medianCents(sorted), high: sorted[sorted.length - 1] as number };
}

/**
 * Per piece type, deterministic. Throws on a piece type or source kind outside the closed sets: migration
 * 0065's CHECKs make that impossible, so meeting one means the evidence is not what this code believes (section 0).
 * The position compares the shop's MIDDLE price with the reported low and high: below the low, above the high,
 * or within. It is advice to the owner and nothing else reads it (T3: no price moves from here).
 *
 * TA-1361: n counts distinct customers, never messages (section 0.6: silent below n = 5). The point of the floor
 * is that no single customer's claim is ever reported back, so one customer's five priced messages are one price.
 * The unit is the contact, not the thread: one contact can hold one thread per channel (B13). Each customer keeps
 * one price per piece type, the newest (the loader hands rows over oldest first, so the last one wins), and the
 * floor and the median read only those.
 */
export function competitorPieceSummaries(mentions: readonly CompetitorMentionFact[], shopPrices: readonly ShopPriceFact[]): CompetitorPieceSummary[] {
  for (const f of mentions) {
    if (!isCompetitorPieceType(f.pieceType)) throw new Error(`competitor_prices read a piece type outside the closed set: ${f.pieceType}`);
    if (!isCompetitorSourceKind(f.sourceKind)) throw new Error(`competitor_prices read a source kind outside the closed set: ${f.sourceKind}`);
    if (f.claimedPriceCents !== null && (!Number.isSafeInteger(f.claimedPriceCents) || f.claimedPriceCents <= 0)) {
      throw new Error("competitor_prices read a claimed price that is not positive integer cents");
    }
  }
  const out: CompetitorPieceSummary[] = [];
  for (const piece of COMPETITOR_PIECE_TYPES) {
    const mine = mentions.filter((f) => f.pieceType === piece);
    if (mine.length === 0) continue;
    const customers = (fs: readonly CompetitorMentionFact[]): number => new Set(fs.map((f) => f.contactId)).size;
    const bySourceKind = Object.fromEntries(COMPETITOR_SOURCE_KINDS.map((k) => [k, customers(mine.filter((f) => f.sourceKind === k))])) as Record<
      CompetitorSourceKind,
      number
    >;
    const names = new Map<string, { name: string; contacts: Set<string> }>();
    for (const f of mine) {
      if (f.sourceName === null) continue;
      const key = f.sourceName.trim().toLowerCase();
      // The first spelling seen names the group; the loader hands rows over in a fixed order.
      const cur = names.get(key) ?? { name: f.sourceName.trim(), contacts: new Set<string>() };
      cur.contacts.add(f.contactId);
      names.set(key, cur);
    }
    const topSources = [...names.values()]
      .map((g) => ({ name: g.name, count: g.contacts.size }))
      .sort((a, b) => b.count - a.count || a.name.toLowerCase().localeCompare(b.name.toLowerCase()) || a.name.localeCompare(b.name))
      .slice(0, COMPETITOR_TOP_SOURCES);
    // One price per customer, the newest: oldest first in, so a later price overwrites an earlier one (TA-1361).
    const newestPrice = new Map<string, number>();
    for (const f of mine) if (f.claimedPriceCents !== null) newestPrice.set(f.contactId, f.claimedPriceCents);
    const prices = [...newestPrice.values()];
    const reported = centsSpread(prices);
    const own = shopPrices.filter((s) => s.pieceType === piece).map((s) => s.priceCents);
    const ownSpread = centsSpread(own);
    const yours = ownSpread === null ? null : { ...ownSpread, n: own.length };
    const position: CompetitorPosition | null =
      reported === null || yours === null ? null : yours.median < reported.low ? "below" : yours.median > reported.high ? "above" : "within";
    out.push({ pieceType: piece, mentions: customers(mine), bySourceKind, topSources, priced: prices.length, reported, yours, yoursPriced: own.length, position });
  }
  const order = (p: CompetitorPieceType) => COMPETITOR_PIECE_TYPES.indexOf(p);
  return out.sort((a, b) => b.mentions - a.mentions || order(a.pieceType) - order(b.pieceType));
}

/**
 * The distinct customers behind a set of mentions, across every piece type: the `competitor_prices` section's n.
 * A customer who named shops for a ring and a necklace is one customer here and one in each piece type, so this
 * is never the sum of the pieces' `mentions` (TA-1361).
 */
export function competitorCustomers(mentions: readonly CompetitorMentionFact[]): number {
  return new Set(mentions.map((f) => f.contactId)).size;
}

/**
 * The digest's and the Q&A's cells. Codes, counts and cents only: a source NAME is customer-typed, and the
 * `digest.analyst` and `analyst.answered` rows are events, which outlive erasure (section 10), so no name is
 * ever a cell key. The names are read live from the table on the Results page, where erasure reaches them.
 *
 * `customers` is `competitorCustomers` over the same mentions, the section's n (TA-1361). A total below the largest
 * piece's count or above their sum cannot come from those mentions, so it throws rather than label a wrong n
 * (section 0).
 */
export function competitorPriceRows(summaries: readonly CompetitorPieceSummary[], customers: number): QueryResult {
  const counts = summaries.map((s) => s.mentions);
  if (!Number.isSafeInteger(customers) || customers < Math.max(0, ...counts) || customers > sum(counts)) {
    throw new Error(`competitor_prices was handed ${customers} customers, which the piece counts (${counts.join(", ")}) cannot hold`);
  }
  const rows: AggregateRow[] = [];
  for (const s of summaries) {
    const p = s.pieceType;
    rows.push(measuredRow(`${p}:mentions`, s.mentions, s.mentions, "count"));
    for (const k of COMPETITOR_SOURCE_KINDS) {
      const c = s.bySourceKind[k];
      if (c > 0) rows.push(measuredRow(`${p}:from_${k}`, c, c, "count"));
    }
    rows.push(measuredRow(`${p}:priced`, s.priced, s.priced, "count"));
    rows.push(measuredRow(`${p}:reported_low`, s.priced, s.reported?.low ?? null, "cents"));
    rows.push(measuredRow(`${p}:reported_median`, s.priced, s.reported?.median ?? null, "cents"));
    rows.push(measuredRow(`${p}:reported_high`, s.priced, s.reported?.high ?? null, "cents"));
    // TA-1361: n is the shop's priced products of this kind even below the floor, as `priced` is for the reported
    // side, so the owner's page never reads "from 0" when the catalog holds three (section 4).
    const yn = s.yoursPriced;
    rows.push(measuredRow(`${p}:yours_low`, yn, s.yours?.low ?? null, "cents"));
    rows.push(measuredRow(`${p}:yours_median`, yn, s.yours?.median ?? null, "cents"));
    rows.push(measuredRow(`${p}:yours_high`, yn, s.yours?.high ?? null, "cents"));
    if (s.position !== null) rows.push(measuredRow(`${p}:yours_sit_${s.position}`, s.priced, 1, "count"));
  }
  return result("competitor_prices", rows, customers, "count");
}

const PIECE_PLURAL: Readonly<Record<CompetitorPieceType, string>> = {
  engagement_ring: "Engagement rings",
  wedding_band: "Wedding bands",
  ring: "Rings",
  necklace: "Necklaces",
  pendant: "Pendants",
  bracelet: "Bracelets",
  earrings: "Earrings",
  watch: "Watches",
  other: "Other pieces",
};

/** The owner's plural name for a piece type. */
export function competitorPieceName(p: CompetitorPieceType): string {
  return PIECE_PLURAL[p];
}

const POSITION_WORDS: Readonly<Record<CompetitorPosition, string>> = {
  below: "Yours sit below that range.",
  within: "Yours sit within that range.",
  above: "Yours sit above that range.",
};

/**
 * The owner's one line per piece type. Plain words, every figure from `formatCents` (F-16), no advice to change
 * a price and no offer word (section 0.5; pinned against `holdsOfferWording` by competitorPrices.test.ts).
 */
export function competitorPieceLine(s: CompetitorPieceSummary): string {
  // The counts are customers, never messages (TA-1361), so the line says so.
  const head = `${PIECE_PLURAL[s.pieceType]}: ${s.mentions} ${s.mentions === 1 ? "customer" : "customers"} named another shop or site`;
  if (s.priced === 0) return `${head}; none gave a price.`;
  if (s.reported === null) return `${head}; ${s.priced} gave a price, not enough yet to show a range (needs ${ANALYST_SAMPLE_FLOOR}).`;
  const range = `${head}; ${s.priced} gave a price, from ${formatCents(s.reported.low)} to ${formatCents(s.reported.high)} (middle ${formatCents(s.reported.median)}).`;
  if (s.position === null) return `${range} Your own range for these is not measured yet, so there is no comparison.`;
  return `${range} ${POSITION_WORDS[s.position]}`;
}
